import { Assessment, Settings, Upload } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Container,
    Grid,
    Paper,
    Typography
} from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import VttUploader from '../components/Admin/VttUploader';
import { RootState } from '../types';

const AdminPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { items: interviews } = useSelector((state: RootState) => state.interviews);
  const { items: insights } = useSelector((state: RootState) => state.insights);

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            管理者ダッシュボード
          </Typography>
          <Typography variant="body1" color="text.secondary">
            ようこそ、{user?.name}さん
          </Typography>
          <Chip
            label="管理者"
            color="primary"
            size="small"
            sx={{ mt: 1 }}
          />
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Assessment sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">
                    インタビュー数
                  </Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {interviews.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  件のインタビューが登録されています
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Assessment sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="h6">
                    インサイト数
                  </Typography>
                </Box>
                <Typography variant="h4" color="secondary">
                  {insights.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  件のインサイトが生成されています
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Upload sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6">
                    最近のアップロード
                  </Typography>
                </Box>
                <Typography variant="h4" color="success.main">
                  {interviews.filter(interview => {
                    const interviewDate = new Date(interview.date);
                    const oneWeekAgo = new Date();
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                    return interviewDate >= oneWeekAgo;
                  }).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  件（過去7日間）
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3 }}>
              <VttUploader />
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                管理機能
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                システムの管理機能にアクセスできます。
              </Typography>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <Settings sx={{ verticalAlign: 'middle', mr: 1 }} />
                    システム設定
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    タグ管理、ユーザー管理などの設定
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" disabled>
                    設定を開く（準備中）
                  </Button>
                </CardActions>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <Assessment sx={{ verticalAlign: 'middle', mr: 1 }} />
                    レポート出力
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    インサイトの分析レポートを出力
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" disabled>
                    レポート作成（準備中）
                  </Button>
                </CardActions>
              </Card>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                使用方法
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>1. .vttファイルの準備</strong><br />
                Microsoft Teamsの会議記録から.vttファイルをダウンロードしてください。
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>2. ファイルのアップロード</strong><br />
                左側のアップロード領域にファイルをドラッグ&ドロップするか、クリックして選択してください。
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>3. 自動処理</strong><br />
                アップロードされたファイルは自動的に解析され、インタビューとインサイトが生成されます。
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AdminPage;
